import React from "react";
import "./crm-benfits.scss";
import { Box, TextField } from "@mui/material";
import {
  CheckboxInputComponent,
  TextInputComponent,
} from "../../../../../common/components";
import { Form, Formik, useFormikContext } from "formik";
import SearchableAutoSelect from "../../../../../common/components/auto-select-search-component/search-select-component";
import CustomRadioGroup from "../../../../../common/components/radio-button-component/radio-button-component";
import { AntSwitch } from "../../../../user-management/user-management-list/components/ant-switch";
import { storageDropdown } from "../../crm-plans-utils";

const labelSwitch = { inputProps: { "aria-label": "Switch demo" } };

interface CRMBenefitsProps {
  mode?: string;
  handleCancel?: any;
  handleSubmit?: (
    sectionType: "basicDetails" | "pricing" | "benefits",
    payload: any,
    updatePlansData: any
  ) => void;
}

const fields = [
  "noOfUsers",
  "storageLimit",
  "noOfLeads",
  "noOfContacts",
  "apiCallsPerMonth",
  "automationRulesLimit",
  "supportLevel",
];

const CRMBenefits: React.FC<CRMBenefitsProps> = ({
  mode,
  handleCancel,
  handleSubmit,
}) => {
  const {
    values,
    validateField,
    setFieldValue,
    handleChange,
    handleBlur,
    setFieldTouched,
  } = useFormikContext<any>();

  const onSubmit = () => {
    const allFieldsValid = fields.every((field) => {
      const value = values?.[field];

      if (field === "supportLevel") {
        return Array.isArray(value) && value.some((opt) => opt.checked);
      }

      return value !== undefined && value !== null && value !== "";
    });
    if (!allFieldsValid) {
      fields.forEach((field) => {
        validateField(field);
        setFieldTouched(field, true);
      });
      return;
    }

    const supprtlevel = values?.supportLevel
      ?.filter((item: any) => item?.checked)
      ?.map((item: any) => item?.name?.toLowerCase());
    const payload: any = {
      notes: values?.notes ?? "",
      noOfUsers: Number(values?.noOfUsers ?? ""),
      noOfLeads: Number(values?.noOfLeads ?? ""),
      noOfContacts: Number(values?.noOfContacts ?? ""),
      storageLimit: values?.storageLimit?.value ?? "",
      apiCallsPerMonth: Number(values?.apiCallsPerMonth ?? ""),
      automationRulesLimit: Number(values?.automationRulesLimit ?? ""),
      supportLevel: supprtlevel?.length ? supprtlevel : null,
      reportingLevel: values?.reportingLevel === "Yes",
    };

    if (handleSubmit)
      handleSubmit("benefits", payload, {
        ...payload,
        supportLevel: values?.supportLevel,
        storageLimit: values?.storageLimit,
        reportingLevel: values?.reportingLevel,
      });
  };

  return (
    <Box className="crm-benefits-wrapper ">
      <p className="crm-benefits-header">Benefits</p>
      <Box
        className="crm-benefits-sub-header"
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1, // spacing between items
        }}
      >
        Enable
        {"   "}
        <AntSwitch
          {...labelSwitch}
          checked={false}
          onChange={(e) => {
            // setFieldValue("status", e.target.checked ? "ACTIVE" : "INACTIVE")
          }}
        />{" "}
        to allow unlimited access for specific benefits in the selected plan.
      </Box>
      <Box>
        <Box
          className="crm-benefits-wrapper-content tenant-edit-basic-details-wrapper"
          gap={5}
        >
          <Box className="crm-benefits-wrapper-content-header">
            <ul className="equal-width-list">
              <li className="list-item">
                <span className="item-label">No of users</span>
                <span className="item-value">
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      {values.noOfUsers === -1 ? (
                        <span>Unlimited</span>
                      ) : (
                        <TextInputComponent
                          value={values.noOfUsers ?? ""}
                          placeholder="Enter number of users"
                          onChange={handleChange}
                          fieldName="noOfUsers"
                          type="text"
                          isNumber
                          maxlength="7"
                        />
                      )}
                    </Box>

                    <AntSwitch
                      {...labelSwitch}
                      checked={values?.noOfUsers == -1}
                      onChange={(e) => {
                        setFieldValue("noOfUsers", e.target.checked ? -1 : "");
                      }}
                      sx={{ marginLeft: "20px" }}
                    />
                  </Box>
                </span>
              </li>
              <li className="list-item">
                <span className="item-label">Data Storage Limit</span>
                <span className="item-value time-zone-select">
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ width: "100%", maxWidth: "200px" }}>
                      <SearchableAutoSelect
                        fieldName="storageLimit"
                        optional
                        options={storageDropdown}
                        isShowError={!values?.storageLimit}
                        placeholder="Select Storage Limit"
                        value={values?.storageLimit}
                        onChange={(e: any, newValue: any) => {
                          setFieldValue("storageLimit", newValue);
                        }}
                        handleBlur={() => {
                          setFieldTouched("storageLimit", true);
                        }}
                      />
                    </Box>
                  </Box>
                </span>
              </li>
              <li className="list-item">
                <span className="item-label">Custom Automation Rules</span>
                <div className="item-value">
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      {values?.automationRulesLimit === -1 ? (
                        <span>Unlimited</span>
                      ) : (
                        <TextInputComponent
                          value={values.automationRulesLimit ?? ""}
                          placeholder="Enter Custom Automation Rules"
                          onChange={handleChange}
                          fieldName="automationRulesLimit"
                          type="text"
                          isNumber
                          maxlength="7"
                        />
                      )}
                    </Box>

                    <AntSwitch
                      {...labelSwitch}
                      checked={values?.automationRulesLimit === -1}
                      onChange={(e) => {
                        setFieldValue(
                          "automationRulesLimit",
                          e.target.checked ? -1 : ""
                        );
                      }}
                      sx={{ marginLeft: "20px" }}
                    />
                  </Box>
                </div>
              </li>
              <li className="list-item">
                <span className="item-label">Reports</span>
                <span className="item-value">
                  <CustomRadioGroup
                    name="export-format"
                    value={values?.reportingLevel || ""}
                    options={[
                      { label: "Yes", value: "Yes" },
                      { label: "No", value: "No" },
                    ]}
                    onChange={(e) => {
                      setFieldValue("reportingLevel", e.target.value);
                    }}
                    row
                  />
                </span>
              </li>
              <li className="list-item">
                <span className="item-label">No of Leads</span>
                <span className="item-value">
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      {values.noOfLeads === -1 ? (
                        <span>Unlimited</span>
                      ) : (
                        <TextInputComponent
                          value={values.noOfLeads ?? ""}
                          placeholder="Enter No of Leads"
                          onChange={handleChange}
                          fieldName="noOfLeads"
                          type="text"
                          isNumber
                          maxlength="7"
                        />
                      )}
                    </Box>
                    <AntSwitch
                      {...labelSwitch}
                      checked={values.noOfLeads === -1}
                      onChange={(e) => {
                        setFieldValue("noOfLeads", e.target.checked ? -1 : "");
                      }}
                      sx={{ marginLeft: "20px" }}
                    />
                  </Box>
                </span>
              </li>
            </ul>
          </Box>
          <Box className="crm-benefits-wrapper-content-header">
            <ul className="equal-width-list">
              <li className="list-item">
                <span className="item-label">No of Contacts</span>
                <span className="item-value">
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      {values.noOfContacts === -1 ? (
                        <span>Unlimited</span>
                      ) : (
                        <TextInputComponent
                          type="text"
                          isNumber
                          maxlength="7"
                          value={values.noOfContacts ?? ""}
                          placeholder="Enter No of Contacts"
                          onChange={handleChange}
                          fieldName="noOfContacts"
                        />
                      )}
                    </Box>
                    <AntSwitch
                      {...labelSwitch}
                      checked={values.noOfContacts === -1}
                      onChange={(e) => {
                        setFieldValue(
                          "noOfContacts",
                          e.target.checked ? -1 : ""
                        );
                      }}
                      sx={{ marginLeft: "20px" }}
                    />
                  </Box>
                </span>
              </li>
              <li className="list-item">
                <span className="item-label">API Calls/month</span>
                <span className="item-value">
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      {values.apiCallsPerMonth === -1 ? (
                        <span>Unlimited</span>
                      ) : (
                        <TextInputComponent
                          value={values.apiCallsPerMonth ?? ""}
                          placeholder="Enter API Calls/month"
                          onChange={handleChange}
                          fieldName="apiCallsPerMonth"
                          type="text"
                          isNumber
                          maxlength="7"
                        />
                      )}
                    </Box>
                    <AntSwitch
                      {...labelSwitch}
                      checked={values.apiCallsPerMonth === -1}
                      onChange={(e) => {
                        setFieldValue(
                          "apiCallsPerMonth",
                          e.target.checked ? -1 : ""
                        );
                      }}
                      sx={{ marginLeft: "20px" }}
                    />
                  </Box>
                </span>
              </li>
              <li className="list-item">
                <span className="item-label">Support</span>
                <span className="item-value checkbox-wrapper">
                  <CheckboxInputComponent
                    fieldName="supportLevel"
                    onChange={(e: any) => {
                      setFieldValue("supportLevel", e);
                    }}
                    value={values?.supportLevel}
                    isMultiSelect
                    listCheckbox={values?.supportLevel}
                  />
                </span>
              </li>
              <li className="list-item">
                <span className="item-label">Add Note</span>
                <span className="item-value">
                  <TextField
                    multiline
                    type="text"
                    rows={4}
                    variant="outlined"
                    fullWidth
                    name="notes"
                    placeholder="Note"
                    className="my-custom-input"
                    onChange={handleChange}
                    value={values?.notes || ""}
                    sx={{
                      maxWidth: "260px",
                    }}
                    slotProps={{
                      input: {
                        inputProps: {
                          maxLength: 150,
                        },
                      },
                    }}
                  />
                </span>
              </li>
            </ul>
          </Box>
        </Box>
      </Box>
      {mode === "edit" && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            paddingBottom: "20px",
            marginTop: "30px",
          }}
        >
          <button
            type="button"
            className="cancel-button"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            className="cancel-button next-button-wrapper"
          >
            Save
          </button>
        </Box>
      )}
    </Box>
  );
};

export default CRMBenefits;
